import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import api, { formatApiError, STATUS_LABELS, ORDER_FLOW } from "../../lib/api";
import { DashboardLayout, StatusBadge, StatCard } from "../../components/Shared";
import LiveMap from "../../components/LiveMap";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import ImageUpload from "../../components/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { LayoutGrid, Bike, Store, AlertTriangle, Send, Users, MapPinned, BadgeCheck, ShoppingBasket, Plus, Pencil, Map as MapIcon, Wallet, Bot, Sparkles, Mail } from "lucide-react";
import BusinessVerification from "../../components/BusinessVerification";
import ServicesModerationPanel from "../../components/services/ServicesModerationPanel";
import TerritoryMap from "../../components/TerritoryMap";
import Payouts from "../../components/Payouts";
import AssistantAdmin from "../../components/AssistantAdmin";
import { toast } from "sonner";

const ERRAND_LABEL = {
  in_attesa: "In attesa", rider_assegnato: "Rider assegnato", in_acquisto: "In acquisto",
  acquistato: "Acquistato", in_consegna: "In consegna", consegnato: "Consegnato", annullato: "Annullato",
};

function OpsErrands() {
  const [errands, setErrands] = useState([]);
  const [riders, setRiders] = useState([]);
  const load = async () => {
    try {
      const [e, r] = await Promise.all([api.get("/ops/errands"), api.get("/ceo/riders")]);
      setErrands(e.data); setRiders(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const assign = async (eid, rid) => { try { await api.put(`/ops/errands/${eid}/assign`, { rider_id: rid }); load(); toast.success("Rider assegnato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleAuth = async (r) => { try { await api.put(`/ceo/riders/${r.id}/capabilities`, { rider_mercato_authorized: !r.rider_mercato_authorized }); load(); toast.success(r.rider_mercato_authorized ? "Autorizzazione revocata" : "Rider autorizzato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const authorized = riders.filter((r) => r.rider_mercato_authorized && !r.suspended);

  return (
    <div className="space-y-8" data-testid="ops-errands">
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><ShoppingBasket className="h-5 w-5 text-[#00E676]" /> Commissioni Rider Mercato</h3>
        <div className="space-y-2">
          {errands.map((e) => (
            <div key={e.id} data-testid={`ops-errand-${e.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">#{e.id.slice(-5)} · {e.customer_name} · <span className="text-zinc-400">{ERRAND_LABEL[e.status] || e.status}</span></div>
                <div className="text-xs text-zinc-500 truncate">{e.address} · {e.items.length} prodotti{e.total > 0 ? ` · €${Number(e.total).toFixed(2)}` : ""}{e.rider_name ? ` · Rider: ${e.rider_name}` : ""}</div>
              </div>
              {["in_attesa", "rider_assegnato"].includes(e.status) && (
                <Select onValueChange={(v) => assign(e.id, v)}>
                  <SelectTrigger data-testid={`assign-errand-${e.id}`} className="w-56 bg-[#0A0A0C] border-white/10 h-9"><SelectValue placeholder="Assegna Rider Mercato" /></SelectTrigger>
                  <SelectContent>{authorized.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          ))}
          {errands.length === 0 && <div className="text-zinc-500 py-6 text-center">Nessuna commissione.</div>}
        </div>
      </div>
      <div>
        <h3 className="font-semibold mb-3">Autorizzazione Rider Mercato</h3>
        <div className="space-y-2">
          {riders.filter((r) => !r.suspended).map((r) => (
            <div key={r.id} data-testid={`rider-cap-${r.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4 flex items-center justify-between">
              <div><div className="font-medium">{r.name}</div><div className="text-xs text-zinc-500">{r.email} · {r.zone}</div></div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${r.rider_mercato_authorized ? "text-[#00E676]" : "text-zinc-500"}`}>{r.rider_mercato_authorized ? "Autorizzato" : "Non autorizzato"}</span>
                <Switch data-testid={`toggle-cap-${r.id}`} checked={!!r.rider_mercato_authorized} onCheckedChange={() => toggleAuth(r)} />
              </div>
            </div>
          ))}
          {riders.length === 0 && <div className="text-zinc-500 py-6 text-center">Nessun rider registrato.</div>}
        </div>
      </div>
    </div>
  );
}

const NAV = [
  { key: "overview", label: "Panoramica", path: "/ops", icon: LayoutGrid },
  { key: "map", label: "Mappa", path: "/ops/map", icon: MapPinned },
  { key: "livemap", label: "Mappa viva", path: "/ops/livemap", icon: MapIcon },
  { key: "verification", label: "Verifica attività", path: "/ops/verification", icon: BadgeCheck },
  { key: "servizi", label: "Servizi Esperienze", path: "/ops/servizi", icon: Sparkles },
  { key: "riders", label: "Rider", path: "/ops/riders", icon: Bike },
  { key: "businesses", label: "Attività", path: "/ops/businesses", icon: Store },
  { key: "liquidazioni", label: "Liquidazioni", path: "/ops/liquidazioni", icon: Wallet },
  { key: "mercato", label: "Rider Mercato", path: "/ops/mercato", icon: ShoppingBasket },
  { key: "assistente", label: "Assistente AI", path: "/ops/assistente", icon: Bot },
  { key: "notify", label: "Notifiche", path: "/ops/notify", icon: Send },
];

function useOverview() {
  const [data, setData] = useState(null);
  const load = async () => { const { data } = await api.get("/ops/overview"); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  return [data, load];
}

function Overview() {
  const [data, load] = useOverview();
  const [zone, setZone] = useState("all");
  const [status, setStatus] = useState("all");
  if (!data) return null;
  const riders = data.riders.filter((r) => !r.suspended);

  const assign = async (oid, rid) => { try { await api.put(`/orders/${oid}/assign`, { rider_id: rid }); load(); toast.success("Rider assegnato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const changeStatus = async (oid, s) => { try { await api.put(`/orders/${oid}/status`, { status: s }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const refund = async (oid) => { if (!window.confirm("Rimborsare questo ordine?")) return; try { await api.post(`/payments/refund/${oid}`); load(); toast.success("Rimborso eseguito"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  let orders = data.orders;
  if (zone !== "all") orders = orders.filter((o) => o.zone === zone);
  if (status !== "all") orders = orders.filter((o) => o.status === status);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ops-total-orders" label="Ordini totali" value={data.orders.length} icon={LayoutGrid} />
        <StatCard testid="ops-late" label="In ritardo" value={data.late_orders.length} icon={AlertTriangle} accent={data.late_orders.length > 0} />
        <StatCard testid="ops-available-riders" label="Rider disponibili" value={data.available_riders} icon={Bike} />
        <StatCard testid="ops-busy-riders" label="Rider occupati" value={data.busy_riders} icon={Bike} />
      </div>
      <div className="flex gap-3">
        <Select value={zone} onValueChange={setZone}><SelectTrigger data-testid="ops-zone-filter" className="w-56 bg-[#1C1C1F] border-white/10"><SelectValue placeholder="Zona" /></SelectTrigger><SelectContent><SelectItem value="all">Tutte le zone</SelectItem>{data.zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="ops-status-filter" className="w-56 bg-[#1C1C1F] border-white/10"><SelectValue placeholder="Stato" /></SelectTrigger><SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{ORDER_FLOW.concat(["annullato"]).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="overflow-x-auto bg-[#1C1C1F] border border-white/10 rounded-lg">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-zinc-500 text-left"><th className="p-3">Ordine</th><th className="p-3">Cliente</th><th className="p-3">Attività</th><th className="p-3">Zona</th><th className="p-3">Stato</th><th className="p-3">Rider</th><th className="p-3">Azioni</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`ops-order-${o.id}`} className="border-b border-white/5">
                <td className="p-3">#{o.id.slice(-5)}</td><td className="p-3">{o.customer_name}</td><td className="p-3">{o.business_name}</td><td className="p-3">{o.zone}</td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
                <td className="p-3">{o.rider_name || "—"}</td>
                <td className="p-3">
                  <div className="flex gap-2 items-center">
                    <Select onValueChange={(v) => assign(o.id, v)}><SelectTrigger data-testid={`assign-rider-${o.id}`} className="w-36 h-8 bg-[#0A0A0C] border-white/10 text-xs"><SelectValue placeholder="Assegna" /></SelectTrigger><SelectContent>{riders.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.available ? " ✓" : ""}</SelectItem>)}</SelectContent></Select>
                    <Select onValueChange={(v) => changeStatus(o.id, v)}><SelectTrigger data-testid={`ops-status-${o.id}`} className="w-32 h-8 bg-[#0A0A0C] border-white/10 text-xs"><SelectValue placeholder="Stato" /></SelectTrigger><SelectContent>{ORDER_FLOW.concat(["annullato"]).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent></Select>
                    {o.payment_status === "paid" && <Button size="sm" variant="destructive" data-testid={`refund-${o.id}`} onClick={() => refund(o.id)} className="h-8 text-xs">Rimborsa</Button>}
                    {o.payment_status === "refunded" && <span className="text-xs text-cyan-400">rimborsato</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MapView() {
  const [data] = useOverview();
  const [riders, setRiders] = useState([]);
  useEffect(() => {
    const load = async () => { try { const { data } = await api.get("/riders/live"); setRiders(data); } catch {} };
    load(); const t = setInterval(load, 8000); return () => clearInterval(t);
  }, []);
  if (!data) return null;
  const active = data.orders.filter((o) => !["consegnato", "annullato"].includes(o.status));
  const RIDER_COLOR = { in_consegna: "#22D3EE", disponibile: "#00E676", offline: "#6b7280" };
  const points = [];
  riders.forEach((r) => {
    if (r.location && typeof r.location.lat === "number") {
      points.push({ id: r.id, lat: r.location.lat, lng: r.location.lng, label: r.name.split(" ")[0], type: "rider", color: RIDER_COLOR[r.status] || "#00E676" });
    }
  });
  (data.businesses || []).forEach((b) => {
    if (typeof b.lat === "number") {
      points.push({ id: `biz-${b.id || b.name}`, lat: b.lat, lng: b.lng, label: (b.name || "").split(" ")[0], type: "business" });
    }
  });
  const ridersOnMap = points.filter((p) => p.type === "rider").length;
  return (
    <div className="space-y-4">
      <div className="flex gap-4 text-sm flex-wrap">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-[#22D3EE]" /> In consegna</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-[#00E676]" /> Disponibile</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-zinc-500" /> Offline</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded-full bg-amber-400" /> Attività</span>
      </div>
      <LiveMap height={440} points={points} />
      <div className="text-zinc-400 text-sm" data-testid="ops-map-summary">{active.length} ordini attivi · {ridersOnMap} rider con posizione GPS live.</div>
      {ridersOnMap === 0 && <div className="text-xs text-zinc-500">Nessun rider sta condividendo la posizione GPS in questo momento.</div>}
    </div>
  );
}

function Riders() {
  const [data, load] = useOverview();
  if (!data) return null;
  const suspend = async (id, val) => { await api.put(`/admin/suspend/${id}`, { suspended: val }); load(); toast.success(val ? "Sospeso" : "Riattivato"); };
  return (
    <div className="space-y-2">
      {data.riders.map((r) => (
        <div key={r.id} data-testid={`ops-rider-${r.id}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
          <div><div className="font-medium">{r.name} {r.suspended && <span className="text-red-400 text-xs">(sospeso)</span>}</div><div className="text-sm text-zinc-500">{r.email} · {r.available ? "disponibile" : "occupato"}</div></div>
          <Button size="sm" variant={r.suspended ? "secondary" : "destructive"} data-testid={`suspend-rider-${r.id}`} onClick={() => suspend(r.id, !r.suspended)}>{r.suspended ? "Riattiva" : "Sospendi"}</Button>
        </div>
      ))}
    </div>
  );
}

const BIZ_EMPTY = { name: "", contact_name: "", macro_category: "", category: "", subcategory: "", zone: "", address: "", phone: "", description: "", image: "", active: true };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ACC_STATUS = {
  non_attivata: { dot: "#F59E0B", label: "Non attivata" },
  invito_inviato: { dot: "#FB923C", label: "Invito inviato" },
  attiva: { dot: "#00E676", label: "Attiva" },
  sospesa: { dot: "#EF4444", label: "Sospesa" },
};
function AccountBadge({ status }) {
  const s = ACC_STATUS[status] || ACC_STATUS.non_attivata;
  return (
    <span data-testid={`acc-status-${status}`} className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-white/15 bg-white/5">
      <span className="h-2 w-2 rounded-full" style={{ background: s.dot }} /> {s.label}
    </span>
  );
}

function Businesses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BIZ_EMPTY);
  const [taxonomy, setTaxonomy] = useState([]);
  const [acct, setAcct] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [confirmAccount, setConfirmAccount] = useState(null);
  const load = () => api.get("/admin/businesses").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/taxonomy").then(({ data }) => setTaxonomy(data.taxonomy || [])).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cats = (taxonomy.find((t) => t.macro === form.macro_category)?.categories) || [];
  const subs = (cats.find((c) => c.name === form.category)?.subs) || [];
  const openNew = () => { setEditing(null); setForm(BIZ_EMPTY); setAcct(null); setOwnerEmail(""); setConfirmAccount(null); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b.id);
    setForm({ name: b.name || b.brand_name || "", contact_name: b.contact_name || "", macro_category: b.macro_category || "", category: b.category || "", subcategory: b.subcategory || "", zone: b.zone || "", address: b.address || "", phone: b.phone || "", description: b.description || "", image: b.image || "", active: !b.suspended });
    setAcct({ account_status: b.account_status, owner_email: b.owner_email, invite_sent_at: b.invite_sent_at, owner_last_login: b.owner_last_login });
    setOwnerEmail(b.owner_email || ""); setConfirmAccount(null); setOpen(true);
  };
  const resend = async (b) => {
    try { await api.post(`/admin/businesses/${b.id}/resend-invite`); load(); toast.success("Invito reinviato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendOwner = async (confirm = false) => {
    if (!EMAIL_RE.test(ownerEmail.trim())) return toast.error("Inserisci un'email valida");
    try {
      const { data } = await api.post(`/admin/businesses/${editing}/set-owner`, { email: ownerEmail.trim(), confirm_link: confirm });
      if (data.result === "requires_confirmation") { setConfirmAccount(data.account); return; }
      setConfirmAccount(null);
      setAcct({ account_status: data.account_status, owner_email: data.owner_email, invite_sent_at: data.invite_sent_at, owner_last_login: data.owner_last_login });
      load(); toast.success(data.result === "linked" ? "Account collegato all'attività" : "Invito di attivazione inviato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Il nome è obbligatorio");
    try {
      if (editing) await api.put(`/admin/businesses/${editing}`, form);
      else await api.post("/admin/businesses", form);
      setOpen(false); load(); toast.success(editing ? "Attività aggiornata" : "Attività creata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (b) => {
    try { await api.put(`/admin/businesses/${b.id}`, { active: b.suspended }); load(); toast.success(b.suspended ? "Attività attivata" : "Attività disattivata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setTier = async (b, tier) => {
    try { await api.put(`/admin/businesses/${b.id}/visibility`, { tier }); load(); toast.success(`Visibilità aggiornata: ${tier}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="ops-businesses">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold flex items-center gap-2"><Store className="h-5 w-5 text-[#00E676]" /> Gestione attività</h3>
        <Button data-testid="biz-new-btn" onClick={openNew} className="bg-[#00E676] text-black rounded-md"><Plus className="h-4 w-4 mr-1" /> Nuova attività</Button>
      </div>
      <div className="space-y-2">
        {items.map((b) => (
          <div key={b.id} data-testid={`ops-biz-${b.id}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-4 gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-medium flex items-center gap-2">{b.name} {b.suspended && <span className="text-red-400 text-xs">(disattivata)</span>}</div>
              <div className="text-sm text-zinc-500 truncate">{[b.macro_category, b.category, b.subcategory].filter(Boolean).join(" › ") || "—"} · {b.zone} · {b.lat != null ? "📍 sulla mappa" : "⚠️ senza posizione"} · {b.products_count || 0} prodotti</div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <AccountBadge status={b.account_status} />
                {b.account_status === "invito_inviato" && b.owner_email && <button data-testid={`biz-resend-${b.id}`} onClick={() => resend(b)} className="text-xs text-[#00E676] hover:underline">Invia di nuovo</button>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={b.visibility_tier || "standard"} onValueChange={(v) => setTier(b, v)}>
                <SelectTrigger data-testid={`biz-tier-${b.id}`} className="h-8 w-[118px] bg-[#0A0A0C] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">🟢 Standard</SelectItem>
                  <SelectItem value="premium">🔵 Premium</SelectItem>
                  <SelectItem value="sponsor">🟡 Sponsor</SelectItem>
                </SelectContent>
              </Select>
              <span className={`text-xs ${!b.suspended ? "text-[#00E676]" : "text-zinc-500"}`}>{b.suspended ? "Off" : "On"}</span>
              <Switch data-testid={`biz-active-${b.id}`} checked={!b.suspended} onCheckedChange={() => toggleActive(b)} />
              <Button size="sm" variant="secondary" data-testid={`biz-edit-${b.id}`} onClick={() => openEdit(b)}><Pencil className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 py-8 text-center">Nessuna attività. Crea la prima direttamente dalla Centrale.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131316] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica attività" : "Nuova attività"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="biz-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div><Label>Referente (facoltativo)</Label><Input data-testid="biz-contact" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Nome del referente" /></div>
            <div><Label>Telefono</Label><Input data-testid="biz-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Macro-categoria</Label>
                <Select value={form.macro_category} onValueChange={(v) => setForm((f) => ({ ...f, macro_category: v, category: "", subcategory: "" }))}>
                  <SelectTrigger data-testid="biz-macro" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue placeholder="Scegli" /></SelectTrigger>
                  <SelectContent>{taxonomy.map((t) => <SelectItem key={t.macro} value={t.macro}>{t.macro}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: "" }))} disabled={!form.macro_category}>
                  <SelectTrigger data-testid="biz-category" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue placeholder="Scegli" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Sottocategoria</Label>
                <Select value={form.subcategory} onValueChange={(v) => set("subcategory", v)} disabled={subs.length === 0}>
                  <SelectTrigger data-testid="biz-subcategory" className="bg-[#0A0A0C] border-white/10 mt-1"><SelectValue placeholder={subs.length ? "Scegli" : "—"} /></SelectTrigger>
                  <SelectContent>{subs.map((s) => <SelectItem key={s.name || s} value={s.name || s}>{s.name || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Comune / Zona</Label><Input data-testid="biz-zone" value={form.zone} onChange={(e) => set("zone", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="San Maurizio Canavese" /></div>
            <div><Label>Indirizzo (per la posizione sulla mappa)</Label><Input data-testid="biz-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" placeholder="Via Roma 1" /></div>
            <div><Label>Descrizione</Label><Textarea data-testid="biz-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={2} /></div>
            <div><Label>Immagine</Label><div className="mt-1"><ImageUpload testid="biz-image" folder="businesses" value={form.image || ""} onChange={(url) => set("image", url)} /></div></div>
            {editing && (
              <div className="rounded-xl border border-white/10 bg-[#0A0A0C] p-4 space-y-3" data-testid="owner-account-section">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Proprietario / Account</h4>
                  <AccountBadge status={acct?.account_status} />
                </div>
                {acct?.account_status === "attiva" ? (
                  <div className="text-sm text-zinc-400 space-y-1">
                    <div>Email: <span className="text-white">{acct?.owner_email || "—"}</span></div>
                    <div>Ultimo accesso: <span className="text-white">{acct?.owner_last_login ? new Date(acct.owner_last_login).toLocaleString("it-IT") : "mai"}</span></div>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs">Email proprietario</Label>
                      <Input data-testid="owner-email-input" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} className="bg-[#131316] border-white/10 mt-1" placeholder="titolare@attivita.it" />
                    </div>
                    {acct?.invite_sent_at && <div className="text-xs text-zinc-500">Invito inviato il {new Date(acct.invite_sent_at).toLocaleString("it-IT")}</div>}
                    {confirmAccount ? (
                      <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 space-y-2" data-testid="owner-confirm">
                        <p className="text-xs text-amber-200">Esiste già l'account attività «{confirmAccount.name}» ({confirmAccount.email}). Vuoi collegarlo a questa attività?</p>
                        <div className="flex gap-2">
                          <Button data-testid="owner-confirm-link" size="sm" onClick={() => sendOwner(true)} className="bg-[#00E676] text-black">Sì, collega</Button>
                          <Button size="sm" variant="secondary" onClick={() => setConfirmAccount(null)}>Annulla</Button>
                        </div>
                      </div>
                    ) : (
                      <Button data-testid="owner-send-invite" size="sm" onClick={() => sendOwner(false)} className="bg-[#00E676] text-black rounded-md">
                        <Mail className="h-3.5 w-3.5 mr-1" /> {acct?.account_status === "invito_inviato" ? "Invia nuovamente invito" : "Invia invito di attivazione"}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2"><span className="text-sm">Attiva (visibile sulla mappa)</span><Switch data-testid="biz-active-toggle" checked={form.active} onCheckedChange={(v) => set("active", v)} /></div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="biz-save" onClick={save} className="bg-[#00E676] text-black rounded-md flex-1">{editing ? "Salva modifiche" : "Crea attività"}</Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annulla</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Notify() {
  const [msg, setMsg] = useState("");
  const [role, setRole] = useState("customer");
  const send = async () => { if (!msg) return; await api.post("/notifications", { role, message: msg }); setMsg(""); toast.success("Notifica inviata"); };
  return (
    <div className="max-w-lg bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4">
      <h3 className="font-semibold">Invia notifica broadcast</h3>
      <Select value={role} onValueChange={setRole}><SelectTrigger data-testid="notify-role" className="bg-[#0A0A0C] border-white/10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="customer">Clienti</SelectItem><SelectItem value="business">Attività</SelectItem><SelectItem value="rider">Rider</SelectItem></SelectContent></Select>
      <Input data-testid="notify-message" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Messaggio…" className="bg-[#0A0A0C] border-white/10" />
      <Button data-testid="send-notify" onClick={send} className="bg-[#00E676] text-black rounded-md"><Send className="h-4 w-4 mr-1" /> Invia</Button>
    </div>
  );
}

export default function OpsDashboard() {
  return (
    <DashboardLayout title="Centrale Operativa" nav={NAV}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="map" element={<MapView />} />
        <Route path="livemap" element={<div className="space-y-3"><h3 className="font-semibold flex items-center gap-2"><MapIcon className="h-5 w-5 text-[#00E676]" /> Mappa viva del territorio</h3><TerritoryMap height={540} /></div>} />
        <Route path="verification" element={<BusinessVerification />} />
        <Route path="servizi" element={<ServicesModerationPanel />} />
        <Route path="riders" element={<Riders />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="liquidazioni" element={<Payouts />} />
        <Route path="mercato" element={<OpsErrands />} />
        <Route path="assistente" element={<AssistantAdmin />} />
        <Route path="notify" element={<Notify />} />
      </Routes>
    </DashboardLayout>
  );
}
